import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { cachedGet } from "../../lib/apiCache";
import { Input } from "../ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const STATUS_TONE = {
  open: "bg-amber-50 text-amber-800 border-amber-200",
  partial: "bg-sky-50 text-sky-800 border-sky-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-600 text-white border-red-600",
};

export const MODE_LABEL = { cash: "Cash", upi: "UPI", bank: "Bank Transfer", cheque: "Cheque", card: "Card", other: "Other" };
export const PAY_MODES = [["bank", "Bank Transfer"], ["upi", "UPI"], ["cash", "Cash"], ["cheque", "Cheque"], ["card", "Card"]];

// Full-screen sheet on phones, centred card from sm up. Add an sm:max-w-* per dialog.
export const SHEET = "grid-cols-[minmax(0,1fr)] w-full max-w-full h-[100dvh] max-h-[100dvh] rounded-none border-0 p-4 pt-5 pb-[max(1rem,env(safe-area-inset-bottom))] content-start overflow-y-auto overflow-x-hidden sm:h-auto sm:max-h-[92vh] sm:w-[96vw] sm:rounded-2xl sm:border sm:p-6";

export function CancelledWatermark({ show }) {
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden z-10" data-testid="billing-cancelled-watermark">
      <span className="-rotate-[24deg] select-none font-heading font-black text-5xl sm:text-7xl tracking-[0.15em] text-red-600/20 border-[6px] border-red-600/20 rounded-2xl px-6 py-2">CANCELLED</span>
    </div>
  );
}

export function StatusPill({ status, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_TONE[status] || STATUS_TONE.open}`}
    >
      {status}
    </span>
  );
}

// Authenticated fetch → opens PDFs inline, forces download for Excel.
const PDF_CACHE = new Map(); // path → { blob, url, at }

// PDFs open in the in-app viewer (desktop + phones) with Share/Download.
export async function openAuthedFile(path, filename) {
  const isPdf = !/fmt=xlsx/.test(path);
  const name = filename || (isPdf ? "document.pdf" : "export.xlsx");
  try {
    let hit = PDF_CACHE.get(path);
    if (!hit || Date.now() - hit.at > 120_000) {
      const tid = toast.loading(isPdf ? "Opening PDF…" : "Preparing file…");
      const resp = await api.get(path, { responseType: "blob" }).finally(() => toast.dismiss(tid));
      const blob = new Blob([resp.data], { type: isPdf ? "application/pdf" : resp.data.type || "application/octet-stream" });
      if (hit) URL.revokeObjectURL(hit.url);
      hit = { blob, url: URL.createObjectURL(blob), at: Date.now() };
      if (isPdf) PDF_CACHE.set(path, hit);
    }
    if (!isPdf) {
      const a = document.createElement("a");
      a.href = hit.url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(hit.url), 60_000);
    } else {
      window.dispatchEvent(new CustomEvent("adams:pdf", { detail: { url: hit.url, blob: hit.blob, filename: name.endsWith(".pdf") ? name : `${name}.pdf` } }));
    }
  } catch (err) {
    toast.error("Download failed");
  }
}

// Warm the PDF cache in the background so "Plain PDF" opens instantly.
export function prefetchPdf(path) {
  const hit = PDF_CACHE.get(path);
  if (hit && Date.now() - hit.at < 120_000) return;
  api.get(path, { responseType: "blob" }).then((resp) => {
    const blob = new Blob([resp.data], { type: "application/pdf" });
    PDF_CACHE.set(path, { blob, url: URL.createObjectURL(blob), at: Date.now() });
  }).catch(() => {});
}

export function clearPdfCache() {
  PDF_CACHE.forEach((v) => URL.revokeObjectURL(v.url));
  PDF_CACHE.clear();
}
if (typeof window !== "undefined") window.addEventListener("adams:cache-invalidate", clearPdfCache);

const KIND_LABEL = { invoice: "Tax Invoice", receipt: "Payment Receipt", credit_note: "Credit Note" };

export async function shareOnWhatsApp(kind, id, extraLine = "") {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.get(`/billing/share/${kind}/${id}`);
    const digits = String(data.phone || "").replace(/\D/g, "");
    const phone = digits.length === 10 ? `91${digits}` : digits;
    const first = (data.customer_name || "there").split(" ")[0];
    const msg = [
      `Hi ${first},`,
      "",
      `Please find your ${KIND_LABEL[kind]} ${data.number} for ${inr(data.amount)} from Adams:`,
      `${BACKEND}${data.path}`,
      extraLine,
      "",
      "— Adams Event Furniture Rentals",
    ].filter((l) => l !== null).join("\n");
    const link = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    if (win) win.location.href = link;
    else window.open(link, "_blank", "noopener");
  } catch (err) {
    if (win) win.close();
    toast.error(err?.response?.data?.detail || "Could not create share link");
  }
}

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// Cached GET with a 30 s TTL; tab switches render instantly.
export function useBillingFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async (force = false) => {
    if (!url) return;
    setLoading(true);
    try {
      // Stale-while-revalidate: paint cached data instantly, refresh quietly.
      const quick = await cachedGet(url, null, { ttlMs: 30_000, force, swr: !force });
      setData(quick);
      setLoading(false);
      if (!force) {
        const fresh = await cachedGet(url, null, { ttlMs: 30_000 });
        if (fresh !== quick) setData(fresh);
      }
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [url]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, reload };
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export function rangePreset(key) {
  const now = new Date();
  const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const y = now.getFullYear();
  const m = now.getMonth();
  if (key === "month") return [iso(new Date(y, m, 1)), todayISO()];
  if (key === "last_month") return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
  if (key === "quarter") return [iso(new Date(y, m - 2, 1)), todayISO()];
  if (key === "fy") return [iso(new Date(m >= 3 ? y : y - 1, 3, 1)), todayISO()];
  if (key === "all") return ["", ""];
  return [iso(new Date(y, m - 11, 1)), todayISO()];
}

const PRESETS = [
  ["month", "This month"],
  ["last_month", "Last month"],
  ["quarter", "3 months"],
  ["fy", "This FY"],
  ["year", "12 months"],
  ["all", "All"],
];

export function DateRangeBar({ from, to, onChange, testPrefix }) {
  const active = PRESETS.find(([k]) => {
    const [f, t] = rangePreset(k);
    return f === from && t === to;
  })?.[0];
  return (
    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto" data-testid={`${testPrefix}-range`}>
      <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 overflow-x-auto max-w-full">
        {PRESETS.map(([k, label]) => (
          <button
            key={k}
            onClick={() => onChange(...rangePreset(k))}
            className={`shrink-0 px-3 h-8 sm:h-7 rounded-full text-xs font-semibold transition-colors ${
              active === k ? "bg-white text-red-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid={`${testPrefix}-preset-${k}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 w-full sm:w-auto">
        <Input type="date" value={from} max={to || undefined} onChange={(e) => onChange(e.target.value, to)}
          className="h-10 sm:h-9 flex-1 min-w-0 sm:flex-none sm:w-[140px] text-xs" data-testid={`${testPrefix}-from`} />
        <span className="text-slate-400 text-xs">to</span>
        <Input type="date" value={to} min={from || undefined} onChange={(e) => onChange(from, e.target.value)}
          className="h-10 sm:h-9 flex-1 min-w-0 sm:flex-none sm:w-[140px] text-xs" data-testid={`${testPrefix}-to`} />
      </div>
    </div>
  );
}

export function StatCard({ label, value, tone = "text-slate-950", sub, testId, onClick, active }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-white border rounded-2xl p-3 sm:p-4 transition-[box-shadow,border-color,transform] duration-200 ${
        active ? "border-red-700 ring-2 ring-red-700/10" : "border-slate-200"
      } ${onClick ? "hover:-translate-y-0.5 hover:shadow-md" : ""}`}
    >
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">{label}</div>
      <div className={`font-heading font-black text-base sm:text-xl mt-1 break-words ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </Tag>
  );
}

export function SkeletonList({ rows = 5 }) {
  return (
    <div className="space-y-2" data-testid="billing-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-14 rounded-xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 animate-pulse" />
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center bg-white" data-testid="billing-empty">
      {Icon && <Icon className="h-8 w-8 mx-auto text-slate-300" />}
      <div className="font-heading font-bold text-slate-900 mt-2">{title}</div>
      {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function IconBtn({ onClick, title, children, tone = "default", testId, disabled }) {
  const tones = {
    default: "border-slate-200 text-slate-700 hover:bg-slate-100",
    wa: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    danger: "border-red-200 text-red-700 hover:bg-red-50",
  };
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      title={title}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-1 h-10 min-w-10 sm:h-8 sm:min-w-8 px-2.5 sm:px-2 rounded-lg border bg-white text-xs font-semibold transition-colors disabled:opacity-40 ${tones[tone]}`}
    >
      {children}
    </button>
  );
}

export function SearchInput({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative flex-1 min-w-[180px]">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="pl-9 h-11 sm:h-10 rounded-xl text-base sm:text-sm" data-testid={testId} />
    </div>
  );
}
